import { Children, ReactNode } from "react";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import {
  CarouselShell,
  Header,
  ItemWrapper,
  Section,
  Subtitle,
  Title,
  TitleRow,
} from "./styles";

export type RailVariant = "featured" | "standard" | "dual";

export interface SectionRailsProps {
  variant: RailVariant;
  children: ReactNode | ReactNode[];
  title?: string;
  subtitle?: string;
  icon?: ReactNode;
}

const RESPONSIVE: Record<
  RailVariant,
  {
    desktop: { breakpoint: { max: number; min: number }; items: number };
    tablet: { breakpoint: { max: number; min: number }; items: number };
    mobile: { breakpoint: { max: number; min: number }; items: number };
  }
> = {
  featured: {
    desktop: { breakpoint: { max: 3000, min: 1024 }, items: 3 },
    tablet: { breakpoint: { max: 1024, min: 640 }, items: 2 },
    mobile: { breakpoint: { max: 640, min: 0 }, items: 1 },
  },
  standard: {
    desktop: { breakpoint: { max: 3000, min: 1024 }, items: 4 },
    tablet: { breakpoint: { max: 1024, min: 640 }, items: 2 },
    mobile: { breakpoint: { max: 640, min: 0 }, items: 1 },
  },
  dual: {
    desktop: { breakpoint: { max: 3000, min: 1024 }, items: 2 },
    tablet: { breakpoint: { max: 1024, min: 640 }, items: 2 },
    mobile: { breakpoint: { max: 640, min: 0 }, items: 1 },
  },
};

export function SectionRails({
  variant,
  children,
  title,
  subtitle,
  icon,
}: SectionRailsProps) {
  const items = Children.toArray(children);
  const showHeader = Boolean(title || subtitle || icon);

  return (
    <Section>
      {showHeader && (
        <Header>
          {(title || icon) && (
            <TitleRow>
              {icon}
              {title && <Title>{title}</Title>}
            </TitleRow>
          )}
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
        </Header>
      )}

      <CarouselShell $variant={variant as "standard" | "dual"}>
        <Carousel
          additionalTransfrom={0}
          arrows
          removeArrowOnDeviceType={["mobile", "tablet"]}
          autoPlay={false}
          centerMode={false}
          containerClass="section-rails-container"
          draggable
          focusOnSelect={false}
          infinite={false}
          itemClass="section-rails-item"
          keyBoardControl
          minimumTouchDrag={80}
          pauseOnHover
          renderArrowsWhenDisabled={false}
          renderButtonGroupOutside={false}
          renderDotsOutside={false}
          responsive={RESPONSIVE[variant]}
          rewind={false}
          rewindWithAnimation={false}
          rtl={false}
          shouldResetAutoplay
          showDots={false}
          slidesToSlide={1}
          swipeable
        >
          {items.map((child, index) => (
            <ItemWrapper key={index} $variant={variant as "standard" | "dual"}>
              {child}
            </ItemWrapper>
          ))}
        </Carousel>
      </CarouselShell>
    </Section>
  );
}
